import React from 'react';
import { ExamQuestion } from '../types';

interface QuestionCardProps {
    question: ExamQuestion;
    selectedOption?: string;
    onSelectOption: (optionLabel: string) => void;
    onBookmark?: () => void;
    isBookmarked: boolean;
    showCorrectAnswer?: boolean;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({ question, selectedOption, onSelectOption, onBookmark, isBookmarked, showCorrectAnswer }) => {
    const [showSolution, setShowSolution] = React.useState(false);

    // Reset solution view when question changes
    React.useEffect(() => {
        setShowSolution(false);
    }, [question.id]);

    return (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 h-full flex flex-col overflow-y-auto">
            <div className="flex justify-between items-start mb-6 shrink-0">
                <div>
                    <span className="inline-block px-3 py-1 bg-blue-50 text-blue-600 text-xs font-bold rounded-full mb-2">
                        {question.subject} - {question.topic}
                    </span>
                    <h3 className="text-lg font-medium text-gray-900 leading-relaxed">
                        <span className="font-bold mr-2">{question.questionNumber}.</span>
                        {question.text}
                    </h3>
                </div>
                {question.image && (
                    <button className="text-gray-400 hover:text-blue-600 transition-colors" aria-label="Görseli büyüt">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
                        </svg>
                    </button>
                )}
            </div>

            {question.image && (
                <div className="mb-6 rounded-xl overflow-hidden border border-gray-100 shrink-0">
                    <img src={question.image} alt="Soru görseli" className="w-full h-auto max-h-64 object-contain bg-gray-50" />
                </div>
            )}

            <div className="space-y-3 flex-1">
                {question.options.map((option) => {
                    let optionClass = "w-full text-left p-4 rounded-xl border-2 transition-all duration-200 flex items-center group relative ";

                    if (showCorrectAnswer) {
                        if (option.label === question.correctOption) {
                            optionClass += "border-green-500 bg-green-50 text-green-700 font-bold";
                        } else if (selectedOption === option.label) {
                            optionClass += "border-red-500 bg-red-50 text-red-700 font-medium";
                        } else {
                            optionClass += "border-gray-200 text-gray-500 opacity-60";
                        }
                    } else {
                        if (selectedOption === option.label) {
                            optionClass += "border-blue-500 bg-blue-50 text-blue-700 font-medium shadow-md shadow-blue-100";
                        } else {
                            optionClass += "border-gray-100 hover:border-blue-200 hover:bg-gray-50 text-gray-600";
                        }
                    }

                    return (
                        <button
                            key={option.label}
                            onClick={() => !showCorrectAnswer && onSelectOption(option.label)}
                            disabled={showCorrectAnswer}
                            className={optionClass}
                        >
                            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold mr-3 transition-colors ${showCorrectAnswer
                                ? option.label === question.correctOption ? 'bg-green-200 text-green-700' : selectedOption === option.label ? 'bg-red-200 text-red-700' : 'bg-gray-100 text-gray-400'
                                : selectedOption === option.label ? 'bg-blue-200 text-blue-700' : 'bg-gray-200 text-gray-500 group-hover:bg-blue-100 group-hover:text-blue-600'
                                }`}>
                                {option.label}
                            </span>
                            <span className="text-sm">{option.text}</span>

                            {showCorrectAnswer && option.label === question.correctOption && (
                                <span className="absolute right-4 text-green-600 text-sm font-bold flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                    Doğru Cevap
                                </span>
                            )}
                            {showCorrectAnswer && selectedOption === option.label && option.label !== question.correctOption && (
                                <span className="absolute right-4 text-red-500 text-sm font-bold flex items-center gap-1">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                    Yanlış Cevap
                                </span>
                            )}
                        </button>
                    );
                })}


                {/* Solution Section */}
                {showCorrectAnswer && question.solution && (
                    <div className="mt-6 border-t border-gray-100 pt-4 pb-2">
                        <button
                            onClick={() => setShowSolution(!showSolution)}
                            className="flex items-center gap-2 text-indigo-600 font-bold hover:text-indigo-800 transition-colors w-full justify-center p-2 rounded-lg hover:bg-indigo-50"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-transform ${showSolution ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                            {showSolution ? 'Çözümü Gizle' : 'Çözümü Göster'}
                        </button>
                        {showSolution && (
                            <div className="mt-4 p-4 bg-indigo-50 rounded-xl text-indigo-900 text-sm whitespace-pre-line leading-relaxed border border-indigo-100 shadow-inner">
                                <div className="flex items-center gap-2 mb-2 font-bold text-indigo-700">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                                    </svg>
                                    Çözüm
                                </div>
                                {question.solution}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
